/*
 * Sundhedstjek 360 — landing page animation engine
 * Lightweight, no dependencies. Respects prefers-reduced-motion.
 * - [data-st360-reveal]        : fade/slide in on scroll (optional [data-st360-delay] ms)
 * - [data-st360-count]         : count up to [data-target] with optional [data-suffix]/[data-prefix]
 * - [data-st360-draw]          : SVG path draws itself (stroke-dashoffset) when visible
 * - [data-st360-constellation] : generates a living biomarker field of drifting nodes
 */
(function () {
  if (window.__st360Init) return;
  window.__st360Init = true;

  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Scroll reveals (replay both directions) ---------- */
  function initReveals() {
    var els = Array.prototype.slice.call(document.querySelectorAll('[data-st360-reveal]'));
    if (!els.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var el = entry.target;
          if (entry.isIntersecting) {
            var delay = parseInt(el.getAttribute('data-st360-delay') || '0', 10);
            el.__st360t = setTimeout(function () { el.classList.add('is-visible'); }, delay);
          } else {
            clearTimeout(el.__st360t);
            el.classList.remove('is-visible');
          }
        });
      },
      { rootMargin: '0px 0px 15% 0px', threshold: 0.01 }
    );
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- Count up ---------- */
  function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }

  function runCount(el) {
    var target = parseFloat(el.getAttribute('data-target') || '0');
    var prefix = el.getAttribute('data-prefix') || '';
    var suffix = el.getAttribute('data-suffix') || '';
    var dur = parseInt(el.getAttribute('data-duration') || '1600', 10);
    if (reduceMotion) { el.textContent = prefix + target + suffix; return; }
    var start = null;
    function step(ts) {
      if (!start) start = ts;
      var p = Math.min((ts - start) / dur, 1);
      var val = Math.round(target * easeOutCubic(p));
      el.textContent = prefix + val + suffix;
      if (p < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  function initCounts() {
    var els = Array.prototype.slice.call(document.querySelectorAll('[data-st360-count]'));
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) { els.forEach(runCount); return; }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var el = entry.target;
          if (entry.isIntersecting) {
            runCount(el);
          } else {
            // reset so it counts up again next time it scrolls into view
            var prefix = el.getAttribute('data-prefix') || '';
            var suffix = el.getAttribute('data-suffix') || '';
            el.textContent = prefix + '0' + suffix;
          }
        });
      },
      { threshold: 0.6 }
    );
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- SVG draw ---------- */
  function initDraws() {
    var els = Array.prototype.slice.call(document.querySelectorAll('[data-st360-draw]'));
    if (!els.length) return;
    els.forEach(function (el) {
      try {
        var len = el.getTotalLength();
        el.__st360len = len;
        el.style.strokeDasharray = len;
        el.style.strokeDashoffset = reduceMotion ? 0 : len;
      } catch (e) {}
    });
    if (reduceMotion || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var el = entry.target;
          var len = el.__st360len;
          if (len == null) { try { len = el.getTotalLength(); } catch (e) { len = 0; } el.__st360len = len; }
          el.style.transition = 'stroke-dashoffset 1.6s cubic-bezier(0.22, 1, 0.36, 1)';
          el.style.strokeDashoffset = entry.isIntersecting ? '0' : String(len);
        });
      },
      { threshold: 0.3 }
    );
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- Living biomarker constellation ---------- */
  function initConstellation() {
    var hosts = Array.prototype.slice.call(
      document.querySelectorAll('[data-st360-constellation]')
    );
    hosts.forEach(function (host) {
      var labels = (host.getAttribute('data-labels') || '')
        .split('|')
        .filter(Boolean);
      var count = parseInt(host.getAttribute('data-nodes') || '34', 10);
      var svgNS = 'http://www.w3.org/2000/svg';
      var W = 600, H = 600;
      var svg = document.createElementNS(svgNS, 'svg');
      svg.setAttribute('viewBox', '0 0 ' + W + ' ' + H);
      svg.setAttribute('class', 'st360-constellation__svg');
      svg.setAttribute('preserveAspectRatio', 'xMidYMid slice');
      var linkLayer = document.createElementNS(svgNS, 'g');
      var nodeLayer = document.createElementNS(svgNS, 'g');
      svg.appendChild(linkLayer);
      svg.appendChild(nodeLayer);
      host.appendChild(svg);

      var nodes = [];
      var cols = Math.ceil(Math.sqrt(count * W / H));
      var rows = Math.ceil(count / cols);
      var cellW = W / cols;
      var cellH = H / rows;
      var cells = [];
      for (var r = 0; r < rows; r++) {
        for (var c = 0; c < cols; c++) cells.push([c, r]);
      }
      for (var si = cells.length - 1; si > 0; si--) {
        var sj = Math.floor(Math.random() * (si + 1));
        var stmp = cells[si]; cells[si] = cells[sj]; cells[sj] = stmp;
      }
      for (var i = 0; i < count; i++) {
        var col = cells[i][0];
        var row = cells[i][1];
        var n = {
          x: (col + 0.15 + Math.random() * 0.7) * cellW,
          y: (row + 0.15 + Math.random() * 0.7) * cellH,
          vx: (Math.random() - 0.5) * 0.08,
          vy: (Math.random() - 0.5) * 0.08,
          r: 2 + Math.random() * 3,
          el: document.createElementNS(svgNS, 'circle')
        };
        n.el.setAttribute('r', n.r);
        n.el.setAttribute('class', 'st360-node' + (i % 7 === 0 ? ' st360-node--hot' : ''));
        nodeLayer.appendChild(n.el);
        if (labels.length && i % 5 === 0 && labels.length) {
          var t = document.createElementNS(svgNS, 'text');
          t.setAttribute('class', 'st360-node__label');
          t.textContent = labels[(i / 5) % labels.length | 0];
          n.label = t;
          nodeLayer.appendChild(t);
        }
        nodes.push(n);
      }

      var links = [];
      var maxLinks = 60;
      for (var k = 0; k < maxLinks; k++) {
        var ln = document.createElementNS(svgNS, 'line');
        ln.setAttribute('class', 'st360-link');
        linkLayer.appendChild(ln);
        links.push(ln);
      }

      var running = true;
      var io2;
      if ('IntersectionObserver' in window) {
        io2 = new IntersectionObserver(function (entries) {
          running = entries[0].isIntersecting;
          if (running && !reduceMotion) requestAnimationFrame(tick);
        });
        io2.observe(host);
      }

      function tick() {
        if (reduceMotion) { draw(); return; }
        var pad = 60;
        var maxV = 0.14;
        for (var i = 0; i < nodes.length; i++) {
          var n = nodes[i];
          if (n.x < pad) n.vx += 0.005;
          else if (n.x > W - pad) n.vx -= 0.005;
          if (n.y < pad) n.vy += 0.005;
          else if (n.y > H - pad) n.vy -= 0.005;
          n.vx += (Math.random() - 0.5) * 0.002;
          n.vy += (Math.random() - 0.5) * 0.002;
          if (n.vx > maxV) n.vx = maxV; else if (n.vx < -maxV) n.vx = -maxV;
          if (n.vy > maxV) n.vy = maxV; else if (n.vy < -maxV) n.vy = -maxV;
          n.x += n.vx; n.y += n.vy;
          if (n.x < 0) { n.x = 0; n.vx = Math.abs(n.vx); }
          else if (n.x > W) { n.x = W; n.vx = -Math.abs(n.vx); }
          if (n.y < 0) { n.y = 0; n.vy = Math.abs(n.vy); }
          else if (n.y > H) { n.y = H; n.vy = -Math.abs(n.vy); }
        }
        draw();
        if (running) requestAnimationFrame(tick);
      }

      function draw() {
        for (var i = 0; i < nodes.length; i++) {
          var n = nodes[i];
          n.el.setAttribute('cx', n.x);
          n.el.setAttribute('cy', n.y);
          if (n.label) {
            n.label.setAttribute('x', n.x + n.r + 6);
            n.label.setAttribute('y', n.y + 3);
          }
        }
        var li = 0;
        for (var a = 0; a < nodes.length && li < links.length; a++) {
          for (var b = a + 1; b < nodes.length && li < links.length; b++) {
            var dx = nodes[a].x - nodes[b].x;
            var dy = nodes[a].y - nodes[b].y;
            var dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < 140) {
              var ln = links[li++];
              ln.setAttribute('x1', nodes[a].x);
              ln.setAttribute('y1', nodes[a].y);
              ln.setAttribute('x2', nodes[b].x);
              ln.setAttribute('y2', nodes[b].y);
              ln.setAttribute('opacity', (1 - dist / 140) * 0.55);
            }
          }
        }
        for (; li < links.length; li++) links[li].setAttribute('opacity', 0);
      }

      draw();
      if (!reduceMotion) requestAnimationFrame(tick);
    });
  }

  function init() {
    initReveals();
    initCounts();
    initDraws();
    initConstellation();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
